import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { Divider, List, Tag, Popconfirm, Alert } from 'antd';
import SVG from 'react-inlinesvg';
import { Elements, StripeProvider } from 'react-stripe-elements';
import PaymentMethodForm from './PaymentMethodForm';
import app_params from '../../../config/params';
import { LoadingSpinner } from '../presentation/LoadingSpinner';
import {
  CloseOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { analytics } from '../modules/analytics';


export default class UserPayment extends React.Component {
  static propTypes = {
    payment: PropTypes.shape({
      cards: PropTypes.shape({
        sources: PropTypes.array,
        default_source: PropTypes.string,
      }),
      error_message: PropTypes.string,
      status: PropTypes.string.isRequired, // loading -> idle -> updating -> idle
    }).isRequired,

    networkError: PropTypes.bool.isRequired,

    getCards: PropTypes.func.isRequired,
    addCard: PropTypes.func.isRequired,
    clearCardError: PropTypes.func.isRequired,
    makePrimaryCard: PropTypes.func.isRequired,
    deleteCard: PropTypes.func.isRequired,
  };

  componentDidMount() {
    analytics.trackView('/user/payment');
    this.props.getCards();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.networkError && !this.props.networkError) {
      this.props.getCards();
    }
  }

  handleAddCard = (cardToken) => {
    // eventGA({
    //   category: 'account',
    //   action: 'card_add',
    // });
    this.props.addCard(cardToken.id);
  };

  onMakePrimaryCard = (card) => {
    // eventGA({
    //   category: 'account',
    //   action: 'card_make_primary',
    // });
    this.props.makePrimaryCard(card.id);
  };

  onDeleteCard = (card) => {
    // eventGA({
    //   category: 'account',
    //   action: 'card_delete',
    // });
    this.props.deleteCard(card.id);
  };

  handleCardFormFocus = () => {
    const { error_message } = this.props.payment;
    if (error_message) {
      this.props.clearCardError();
    }
  };

  render() {
    const { cards, status, error_message } = this.props.payment;
    const { sources, default_source } = cards;
    const isLoading = (status === 'loading');
    const isEmpty = ('sources' in cards) ? sources.length === 0 : true;

    if (status === 'loading') {
      return (
        <LoadingSpinner />
      );
    }

    const renderCard = (card) => {
      let iconSrc = require('assets/media/payment/generic.svg');

      if (card.brand.startsWith('Visa')) {
        iconSrc = require('assets/media/payment/visa.svg');
      }
      else if (card.brand.startsWith('MasterCard')) {
        iconSrc = require('assets/media/payment/mastercard.svg');
      }
      else if (card.brand.startsWith('American')) {
        iconSrc = require('assets/media/payment/amex.svg');
      }
      else if (card.brand.startsWith('Discover')) {
        iconSrc = require('assets/media/payment/discover.svg');
      }
      else if (card.brand.startsWith('Diners')) {
        iconSrc = require('assets/media/payment/diners.svg');
      }
      else if (card.brand.startsWith('JCB')) {
        iconSrc = require('assets/media/payment/jcb.svg');
      }
      else if (card.brand.startsWith('UnionPay')) {
        iconSrc = require('assets/media/payment/unionpay.svg');
      }

      return (
        <List.Item
          style={{ backgroundColor: 'rgb(245, 246, 250)' }}
          className="px-3"
          key={card.id}
          extra={
            (card.id === default_source) ?
              (
                <React.Fragment>
                  <span className="text-muted" style={{ fontSize: '12px' }}>This card will be charged on your billing cycle</span>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <span style={{ cursor: 'pointer' }} className="text-primary mr-4" onClick={() => this.onMakePrimaryCard(card)}><CheckOutlined /> Make Primary</span>

                  <span style={{ cursor: 'pointer' }} className="text-danger mr-4" >
                    <Popconfirm
                      placement="topLeft"
                      title="Are you sure to delete this card?"
                      onConfirm={() => this.onDeleteCard(card)}
                      okText="Delete"
                      okType="danger"
                      cancelText="Cancel"
                    >
                      <CloseOutlined />
                      Remove
                    </Popconfirm>
                  </span>
                </React.Fragment>
              )
          }
        >
          <span className="text-muted mb-4" style={{ verticalAlign: 'middle', lineHeight: '24px' }}>
            <SVG
              className="mr-2"
              style={{ width: '24px', height: '22px', lineHeight: '22px', verticalAlign: 'top', display: 'inline-block' }}
              src={iconSrc}
            />
            ending in <span className="text-dark">{card.last4}</span> expiring on <strong className="mr-3">{card.exp_month}/{card.exp_year}</strong>
            {(card.id === default_source) ? (
              <Tag color="green">Primary</Tag>
            ) : null}
          </span>
        </List.Item>
      );
    };

    if (isLoading) {
      return <LoadingSpinner />;
    }

    return (

      <React.Fragment>
        <Helmet>
          <title>My Payment</title>
        </Helmet>

        {
          (!isEmpty) ? (
            <React.Fragment>
              <h5 className="mb-4">Your credit and debit cards</h5>
              <List
                className="mb-5"
                dataSource={sources}
                renderItem={renderCard}
              />
            </React.Fragment>
          ) : null
        }

        <div>
          { (cards.length === 0) ? (
            <h5 className="mb-4">Enter your credit or debit card</h5>
          ) : (
            <h5 className="mb-4">Add another card</h5>
          )}

          {
            (error_message) ? (
              <Alert
                message={error_message}
                type="error"
                className="mb-3"
                closable={true}
              />
            ) : null
          }
          <StripeProvider apiKey={app_params.STRIPE.API_KEY}>
            <div className="payment__add__card">
              <Elements>
                <PaymentMethodForm
                  addCard={this.handleAddCard}
                  onFocus={this.handleCardFormFocus}
                />
              </Elements>
            </div>
          </StripeProvider>
          {
            (cards.length === 0) ? (
              <div className="mb-4">
                <Divider />
                <div>What happens next</div>
                <ul style={{ paddingLeft: 18 }}>
                  <li>Your card will be linked to your subscription.</li>
                  <li>You will be charged as per your billing cycle.</li>
                  <li>Charges on your statement will appear from <code>PAGEHITS, INC.</code></li>
                </ul>
              </div>
            ) : null
          }
        </div>
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/presentation/UserPayment.jsx